import { useAsyncData, useRoute } from '#imports'
import { computed } from 'vue'

import type { Rating } from '@backmarket/http-api/src/api-specs-reviews/types/rating'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import { useFetchPickers } from '~/scopes/product-core/composables/useFetch/useFetchPickers/useFetchPickers'
import { useFetchProduct } from '~/scopes/product-core/composables/useFetch/useFetchProduct/useFetchProduct'

export interface OtherSellersDrawerDataOptions {
  productId?: string
  grade?: string | number
  specialOfferType?: string
}

export function useOtherSellersDrawerData(
  options?: OtherSellersDrawerDataOptions,
  route = useRoute(),
) {
  const $httpFetch = useHttpFetch()

  // 1. Try to get data from PDP in-app context
  const { data: pdpProduct, pending: pdpProductPending } =
    useFetchProduct(route)
  const {
    data: pdpPickers,
    selectedOffer: pdpSelectedOffer,
    pending: pdpPickersPending,
  } = useFetchPickers()

  // 2. Resolve URL parameters for webviews or fallback
  const resolvedProductId = computed(() => {
    return (
      options?.productId ??
      (route.params?.productId as string | undefined) ??
      ''
    )
  })

  const resolvedGrade = computed(() => {
    const rawGrade =
      options?.grade ?? (route.query?.grade as string | undefined)
    return rawGrade ? String(rawGrade) : null
  })

  const resolvedSpecialOfferType = computed(() => {
    const rawOffer =
      options?.specialOfferType ??
      (route.query?.specialOfferType as string | undefined)
    return rawOffer ? String(rawOffer) : null
  })

  const isWebviewStandalone = computed(() => {
    // If we're on the webview drawer route or PDP product is not already loaded
    return !pdpProduct.value && !!resolvedProductId.value
  })

  // 3. Fallback standalone fetching for webview route
  const { data: standaloneProduct, pending: standaloneProductPending } =
    useAsyncData(
      () => `other-sellers-webview-product-${resolvedProductId.value}`,
      async () => {
        if (!isWebviewStandalone.value || !resolvedProductId.value) return null
        return await $httpFetch('/product-page/products/:productId', {
          pathParams: { productId: resolvedProductId.value },
        })
      },
      {
        watch: [resolvedProductId],
      },
    )

  const { data: standalonePickers, pending: standalonePickersPending } =
    useAsyncData(
      () =>
        `other-sellers-webview-pickers-${resolvedProductId.value}-${resolvedGrade.value}-${resolvedSpecialOfferType.value}`,
      async () => {
        if (!isWebviewStandalone.value || !resolvedProductId.value) return null
        return await $httpFetch('/product-page/products/:productId/pickers', {
          pathParams: { productId: resolvedProductId.value },
          query: {
            grade: resolvedGrade.value,
            special_offer_type: resolvedSpecialOfferType.value,
          },
        })
      },
      {
        watch: [resolvedProductId, resolvedGrade, resolvedSpecialOfferType],
      },
    )

  const product = computed(() => pdpProduct.value ?? standaloneProduct.value)
  const pickers = computed(() => pdpPickers.value ?? standalonePickers.value)

  const productId = computed(
    () => resolvedProductId.value || product.value?.productId || '',
  )

  const pending = computed(
    () =>
      pdpProductPending.value ||
      pdpPickersPending.value ||
      standaloneProductPending.value ||
      standalonePickersPending.value,
  )

  const productName = computed(() => product.value?.titles?.default ?? '')

  const productImage = computed(() => product.value?.images?.[0]?.url ?? null)

  const productImageAlt = computed(
    () => product.value?.images?.[0]?.description ?? productName.value,
  )

  const selectedGradeLabel = computed(() => {
    const gradeGroup = pickers.value?.pickerGroups?.find(
      (g) => g.id === 'grades',
    )
    return gradeGroup?.items.find((item) => item.selected)?.label ?? null
  })

  const selectedTags = computed(() => {
    const subtitleParts = product.value?.titles?.subtitle?.split(' • ') ?? []
    const tags = selectedGradeLabel.value
      ? [selectedGradeLabel.value, ...subtitleParts]
      : subtitleParts
    return tags.filter(Boolean)
  })

  const selectedOffer = computed(() => {
    return pdpSelectedOffer.value ?? pickers.value?.selectedOffer ?? null
  })

  const merchantId = computed(
    () => selectedOffer.value?.merchant?.merchantId ?? null,
  )

  const { data: sellerRating } = useAsyncData<Rating | null>(
    () => `other-sellers-seller-rating-${merchantId.value ?? 'none'}`,
    () => {
      if (!merchantId.value) return Promise.resolve(null)

      return $httpFetch('/reviews/v1/sellers/:uuid/products/reviews-summary', {
        pathParams: { uuid: merchantId.value },
      })
    },
    {
      server: false,
      lazy: true,
      watch: [merchantId],
    },
  )

  const selectedSellerRating = computed(
    () => sellerRating.value?.averageRate ?? null,
  )

  return {
    product,
    pickers,
    pending,
    productId,
    productName,
    productImage,
    productImageAlt,
    selectedTags,
    selectedOffer,
    selectedSellerRating,
  }
}
